import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { tokenStorage } from '../utils/tokenStorage';
import { initOAuth2 } from '../lib/oauthClient';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  // ── State: in-memory only (no localStorage for auth) ──
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [oauthReady, setOauthReady] = useState(false);
  const refreshTimeoutRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // ── Initialize OAuth2 app token on mount ──
  useEffect(() => {
    initOAuth2()
      .then(() => setOauthReady(true))
      .catch((err) => {
        console.warn('[OAuth2] Init failed, continuing without app token:', err.message || err);
        setOauthReady(true);
      });
  }, []);

  // ── Set auth header on token change ──
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // ── Logout handler (defined first — used by others) ──
  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Non-critical: backend may be unreachable. Log and continue cleanup.
      console.warn('[Auth] Logout API call failed (cleaning up locally):', err.message || err);
    }

    setUser(null);
    setToken(null);
    // Clean any legacy data that may still be in localStorage
    tokenStorage.clearAll();
    // Clear session indicator
    localStorage.removeItem('nannyou_has_session');
    // Clear email verification skip flag
    sessionStorage.removeItem('nannyou_email_skip');
    delete axios.defaults.headers.common['Authorization'];

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
  }, []);

  // ── Refresh token via HttpOnly cookie ──
  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) return null;
    isRefreshingRef.current = true;

    try {
      const res = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      const { access_token, user: userData } = res.data;

      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      if (userData) {
        setUser(userData);
      }

      return access_token;
    } catch (err) {
      console.warn('[Auth] Refresh failed — logging out:', err.message || err);
      handleLogout();
      return null;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [handleLogout]);

  // ── Restore session on mount (after OAuth ready) ──
  // Uses HttpOnly refresh cookie to obtain a fresh access token
  // Only attempts if we have a session indicator (to avoid unnecessary 401 on public pages)
  useEffect(() => {
    if (!oauthReady) return;

    let cancelled = false;

    const restoreSession = async () => {
      // Quick check: if we have a token in memory, no need to refresh immediately
      if (token) {
        setLoading(false);
        return;
      }

      // Check if we have a session indicator (set on login, cleared on logout)
      const hasSessionIndicator = localStorage.getItem('nannyou_has_session') === 'true';
      
      if (!hasSessionIndicator) {
        // No session indicator = first visit or after logout
        // Skip the refresh call to avoid unnecessary 401
        setLoading(false);
        return;
      }

      try {
        // Attempt refresh only if session indicator exists
        const res = await axios.post(`${API}/auth/refresh`, {}, { 
          withCredentials: true,
          // Suppress error toasts for expired sessions
          validateStatus: (status) => status < 500
        });
        
        if (cancelled) return;

        // Only process if refresh was successful
        if (res.status === 200 && res.data) {
          const { access_token, user: userData } = res.data;
          setToken(access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          // Fetch full user data
          if (userData) {
            setUser(userData);
          } else {
            const meRes = await axios.get(`${API}/auth/me`);
            if (!cancelled) setUser(meRes.data);
          }
        } else {
          // Refresh failed (expired or invalid cookie)
          // Clear the session indicator
          localStorage.removeItem('nannyou_has_session');
        }
      } catch {
        // Refresh failed - clear session indicator
        localStorage.removeItem('nannyou_has_session');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    restoreSession();
    return () => { cancelled = true; };
  }, [oauthReady]);

  // ── Login ──
  const handleLogin = useCallback(async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      const { access_token, user: userData } = res.data;

      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Set session indicator for refresh optimization
      localStorage.setItem('nannyou_has_session', 'true');

      // Fetch full user data (includes shared premium check, is_premium, etc.)
      try {
        const meRes = await axios.get(`${API}/auth/me`);
        setUser(meRes.data);
        return { success: true, user: meRes.data };
      } catch {
        // Fallback to login response data if /me fails
        setUser(userData);
        return { success: true, user: userData };
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur de connexion';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  // ── Register ──
  const handleRegister = useCallback(async (email, password, full_name, phone, turnstile_token, role, first_name, last_name) => {
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email,
        password,
        full_name,
        first_name: first_name || null,
        last_name: last_name || null,
        phone: phone || null,
        turnstile_token: turnstile_token || null,
        role: role || 'parent',
      }, { withCredentials: true });
      const { access_token, user: userData } = res.data;

      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Set session indicator for refresh optimization
      localStorage.setItem('nannyou_has_session', 'true');

      // Fetch full user data
      try {
        const meRes = await axios.get(`${API}/auth/me`);
        setUser(meRes.data);
        return { success: true, user: meRes.data };
      } catch {
        setUser(userData);
        return { success: true, user: userData };
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  // ── Refresh user data (e.g., after premium upgrade) ──
  const refreshUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      const userData = res.data;
      setUser(userData);
      return userData;
    } catch (error) {
      console.warn('[Auth] Failed to refresh user:', error.message || error);
      return null;
    }
  }, []);

  // ── Auto-refresh before access token expiry (every 12 min for 15-min tokens) ──
  useEffect(() => {
    if (!token) return;

    refreshTimeoutRef.current = setTimeout(() => {
      refreshToken();
    }, 12 * 60 * 1000);

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [token, refreshToken]);

  // ── Axios response interceptor for 401 — auto-refresh on expired access token ──
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          token &&
          !originalRequest._authRetry &&
          !originalRequest.url?.includes('/auth/refresh') &&
          !originalRequest.url?.includes('/auth/login') &&
          !originalRequest.url?.includes('/oauth/token') &&
          error.response?.data?.error !== 'invalid_token'
        ) {
          originalRequest._authRetry = true;
          const newToken = await refreshToken();
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios.request(originalRequest);
          }
          handleLogout();
          toast.error('Session expirée, veuillez vous reconnecter');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token, refreshToken, handleLogout]);

  const value = {
    user,
    token,
    loading,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    refreshUser,
    refreshToken,
    isAuthenticated: !!token,
    isParent: user?.role === 'parent',
    isNanny: user?.role === 'nanny',
    isPremium: ['premium', 'parent_pro', 'nanny_pro'].includes(user?.current_plan) || user?.is_premium === true,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
